import React, { useContext } from 'react'

//Node Modules
import { Link } from 'react-router-dom'

//Styles
import styles from './ContentLink.module.scss'

//Context
import { DocsetContext, CDCContext, CollectionContext, RequestorContext } from 'context/content-context'
import { LanguageContext } from 'context/language-context'
import { DocsetLatestContext } from 'context/docset-latest-context'

//Components
import PrimaryCdcLink from 'components/PrimaryCdcLink'

// Util
import { collectionLink, cdcLink } from 'util/links'
import { VERSION_GROUP_VIYA4, DOCSET_LATEST_CFR  } from 'util/constants'

interface Props {
  language?: string
  docsetAlias: string
  docsetCFR?: string
  cdcAlias?: string
  cdcCFR?: string
  activeCDC?: string
  page: string
  children: React.ReactNode
  help?: boolean
  id?: string
  className?: string
  target?: string
  forceCFROverride?: boolean,
  tabIndex?: number,
  fragment?: string
}

const ContentLink = ({language, docsetAlias, docsetCFR, cdcAlias, cdcCFR, activeCDC,page, children, help, id, className, target, forceCFROverride, tabIndex = 0, fragment}:Props) => {
  const currentLanguage = useContext(LanguageContext)
  const currentCollection = useContext(CollectionContext)
  const currentCDC = useContext(CDCContext)
  const currentDocset = useContext(DocsetContext)
  const latestCFR = useContext(DocsetLatestContext)
  const requestorId = useContext(RequestorContext)

  let standaloneDocsetLinkNotSelf = false
  let defaultCdcCfr = cdcCFR === 'default'

  // Language provides trumps current language if specified
  const computedLanguage = language || currentLanguage

  let path = undefined

  //Handle click 
  const handleClick=(e: any):void => {
    e.preventDefault()
  }

  // Handle Collection Link
  if (cdcAlias && cdcCFR && activeCDC) {
    let href = collectionLink(computedLanguage, cdcAlias, cdcCFR, activeCDC, docsetAlias, page)
    //check for requestor id
    if (requestorId) {
      href =`${href}?requestorId=${requestorId}`
    }

    //check if there is a fragment
    if (fragment) {
      href = `${href}#${fragment}`
    }

    return (
      defaultCdcCfr ? 
        <a
          href={'.'+href}
          id={id}
          className={className}
          target={target}
          tabIndex={tabIndex}
          onMouseDown={handleClick}
        >
          {children}
        </a>
        :
        <Link 
          to={href} 
          id={id}
          className={className} 
          target={target} 
          tabIndex={tabIndex}
          onMouseDown={handleClick}
        >
          { children }
        </Link>
    )
  }

  // Handle CDC Link
  if (cdcAlias && cdcCFR) {

    let href = cdcLink(computedLanguage, cdcAlias, cdcCFR, docsetAlias, page)
    //check for requestor id
    if (requestorId) {
      href =`${href}?requestorId=${requestorId}`
    }
    //check if there is a fragment
    if (fragment) {
      href = `${href}#${fragment}`
    }
    
    return (
      defaultCdcCfr ? 
        <a
          href={'.'+href}
          id={id}
          className={className}
          target={target}
          tabIndex={tabIndex}
          onMouseDown={handleClick}
        >
          {children}
        </a>
        :
        <Link 
          to={href} 
          id={id}
          className={className} 
          target={target} 
          tabIndex={tabIndex}
          onMouseDown={handleClick}
        >
          { children }
        </Link>
    )
  }

  // Handle Docset Link
  // Check if we are actively in a Collection
  if (currentCollection) {
    // First check if Docset Alias is in Active CDC
    if (currentCDC) {
      // Check if Docset Alias is in Active CDC's Docsets
      const cdcInfo = currentCDC.info

      const docset = cdcInfo.docsetCollection ? cdcInfo.docsetCollection.docsets ? cdcInfo.docsetCollection.docsets.find(d => d ? d.id === docsetAlias : false) : undefined : undefined

      if (docset) {
        path = `${currentCollection.info.id}/${currentCollection.info.version}/${cdcInfo.id}/${docsetAlias}/${page}`
      } else if (help) {
        path = `${currentCollection.info.id}/${currentCollection.info.version}/${cdcInfo.id}/${currentDocset?.id}/${page}`
      }
    }

    // Check if Docset Alias is in ANY CDC in Collection
    if (!path) {
      const cdc = currentCollection.cdcs.find(c => {
        return c.docsetCollection ? c.docsetCollection.docsets ? c.docsetCollection.docsets.find(d => d ? d.id === docsetAlias : false) : [] : []
      })

      if (cdc) {
        path = `${currentCollection.info.id}/${currentCollection.info.version}/${cdc.id}/${docsetAlias}/${page}`
      }
    }
  } else if (currentCDC) { // Check if we are actively in a CDC
    const cdcInfo = currentCDC.info

    // Check if Docset Alias is in CDC's Docsets
    const docset = cdcInfo.docsetCollection.docsets.find(d => d ? d.id === docsetAlias : false)
    if (docset) {
      path = `${cdcInfo.id}/${cdcInfo.version}/${docsetAlias}/${page}`
    } else if (docsetCFR && cdcInfo.versionGroup === VERSION_GROUP_VIYA4 && !help) { //DOCCX-658 Recognize matching CDC software versions when navigating applicable links
      return <PrimaryCdcLink 
        language={computedLanguage}
        id={id}
        className={className}
        target={target}
        tabIndex={tabIndex}
        cdcAlias={cdcInfo.id} 
        cdcVersion={cdcInfo.version} 
        docsetAlias={docsetAlias} 
        docsetVersion={docsetCFR} 
        children={children}
        page={page}
        fragment={fragment}
        help={help}
        handleClick={handleClick}/>
    } else if (help) {
      path = `${cdcInfo.id}/${cdcInfo.version}/${currentDocset?.id}/${page}`
    }
  //else in docset
  } else if (!forceCFROverride && currentDocset && currentDocset.id === docsetAlias) {
    // Check if Docset Alias matches Current Docset Alias
    path = `${docsetAlias}/${!latestCFR || latestCFR !== currentDocset.version ? currentDocset.version : DOCSET_LATEST_CFR }/${page}`
    // ^^^ Note, current Docset CFR overrides any specified from specs by default
  } else if (!forceCFROverride && currentDocset && help) {
    path = `${currentDocset.id}/${!latestCFR || latestCFR !== currentDocset.version ? currentDocset.version : DOCSET_LATEST_CFR }/${page}`
  }

  // If link not build, check if Docset CFR specified
  if (!path && docsetCFR) {
    // Standalone docset links should not use router, so server can handle loading from Primary CDC if available
    if (currentDocset) {
      standaloneDocsetLinkNotSelf = docsetAlias !== currentDocset.id || docsetCFR !== currentDocset.version
      if(standaloneDocsetLinkNotSelf) {
        path = `${docsetAlias}/${docsetCFR}/${page}`
      } else {
        path = `${docsetAlias}/${!latestCFR || latestCFR !== docsetCFR ? docsetCFR : DOCSET_LATEST_CFR }/${page}`
      }
    } else {
      path = `${docsetAlias}/${docsetCFR}/${page}`
      standaloneDocsetLinkNotSelf = true
    }
  }

  let link = `/doc/${computedLanguage}/${path}`

  if (standaloneDocsetLinkNotSelf === true) {
    link = `.${link}`
  }

  // Check if link is to help and if there is a requestor id
  if (help) {
    link = `${link}?help${requestorId ? '&requestorId='+requestorId : ''}`
  } else {
    link = `${link}${requestorId ? '?requestorId='+requestorId : ''}`
  }

  //check if there is a fragment
  if (fragment) {
    link = `${link}#${fragment}`
  }

  return path ?
    !standaloneDocsetLinkNotSelf ? 
      <Link to={link} id={id} className={className} target={target} tabIndex={tabIndex} onMouseDown={handleClick}>{ children }</Link> : 
      <a href={link} id={id} className={className} target={target} tabIndex={tabIndex} onMouseDown={handleClick}>{ children }</a> :
    <span id={id} className={`${className} ${styles.invalid}`}>{ children }</span>
}

export default ContentLink